import { Link, type To } from "react-router-dom";

import {
  requestLogSidebarActiveRailClass,
  requestLogSidebarInactiveHoverRailClass,
} from "@/components/RequestLogsTable/requestLogVisualCues";
import { RequestLogStatus, STATUS_STYLES } from "@/types/request-status";

interface SidebarCardProps {
  id: string;
  active?: boolean;
  type?: string;
  status?: RequestLogStatus;
  /** When true, inactive cards use the same blue hover + rail as the request logs table. */
  requestLogHoverCue?: boolean;
  /** When true and active, use request-log selection chrome (blue rail + tint). Traces/spans should omit this. */
  requestLogSelectionCue?: boolean;
  /** When true, inactive request log cards use the table's recently-viewed tint. */
  requestLogRecentlyViewedCue?: boolean;
  children: React.ReactNode;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  to?: To;
}

type CardStyleOptions = Pick<
  SidebarCardProps,
  | "active"
  | "type"
  | "requestLogHoverCue"
  | "requestLogSelectionCue"
  | "requestLogRecentlyViewedCue"
>;

const getCardStyles = ({
  active = false,
  type,
  requestLogHoverCue = false,
  requestLogSelectionCue = false,
  requestLogRecentlyViewedCue = false,
}: CardStyleOptions): string => {
  if (active) {
    if (requestLogSelectionCue) {
      return type === "group"
        ? "border-blue-500 bg-blue-100 shadow-sm"
        : "border-blue-500 bg-blue-50 shadow-sm";
    }
    return type === "group"
      ? "border-blue-500 bg-blue-100 shadow-sm"
      : "border-blue-500 bg-blue-50 shadow-sm";
  }
  if (requestLogRecentlyViewedCue) {
    return "border-slate-300 bg-slate-50 shadow-sm hover:border-blue-300 hover:bg-blue-50/50";
  }
  if (requestLogHoverCue) {
    return "bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50/50";
  }
  return "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50";
};

const getRequestLogRailClass = (
  active: boolean,
  requestLogHoverCue: boolean,
  requestLogSelectionCue: boolean,
): string => {
  if (active && requestLogSelectionCue) {
    return requestLogSidebarActiveRailClass;
  }
  if (requestLogHoverCue) {
    return requestLogSidebarInactiveHoverRailClass;
  }
  return "";
};

const SidebarCard = ({
  id,
  children,
  active,
  type,
  status,
  requestLogHoverCue = false,
  requestLogSelectionCue = false,
  requestLogRecentlyViewedCue = false,
  className,
  onClick,
  to,
}: SidebarCardProps) => {
  const cardStyle = getCardStyles({
    active,
    type,
    requestLogHoverCue,
    requestLogSelectionCue,
    requestLogRecentlyViewedCue,
  });
  const railClass = getRequestLogRailClass(
    Boolean(active),
    requestLogHoverCue,
    requestLogSelectionCue,
  );

  const combinedClassName = `${cardStyle} ${railClass} relative border cursor-pointer space-y-2 rounded-md py-3 px-3.5 text-xs font-medium text-gray-500 hover:text-gray-600 transition-all duration-150 ${
    className || ""
  }`;

  const cardContent = (
    <div data-request-id={id} className={combinedClassName} onClick={onClick}>
      {status && status !== "SUCCESS" && (
        <div className="absolute right-3 top-[18px] flex items-center justify-center">
          <div
            className={`absolute h-3 w-3 rounded-full opacity-10 ${STATUS_STYLES[status].dot}`}
          />
          <div className={`relative h-1.5 w-1.5 rounded-full ${STATUS_STYLES[status].dot}`} />
        </div>
      )}
      {children}
    </div>
  );

  if (to) {
    return <Link to={to}>{cardContent}</Link>;
  }

  return cardContent;
};

export default SidebarCard;
